/* eslint-disable camelcase */
import { fromJS } from "immutable"
import { createSelector } from "reselect"
import {
    cleanUpHtml,
    generateStaticUrl,
    createDataSections,
    createSectionObject,
    ordinalSuffix,
    createCustomFieldObjects,
    getStatEnumObject,
    getNodeType,
    createGraphToolTip,
    filterStatsFromResponse,
    createProfileInstanceSelector,
    makeImmutableSliceSelector,
    makeObjSliceSelector,
    createToJsSelector,
    createSocialUserObjects,
    lineBreaks,
    makeSourceUrlObjs,
    parseDateFromSelector,
    createExtraFieldSelector,
    deduplicatedObjectArrayToString,
    createContactCardDetailItems,
    getRegionImageUrl,
    createRegionHeaderTitlePrefixSelector,
    mrtPrecedenceSorter,
    editorialPrecedenceSorter,
} from "shared/imports/sharedHelperFunctions"

import { generateSocialUserDetails, generateSocialUserUrl } from "shared/profiles/socialUser/social-user-selectors"
import { selectSocialUsersFromModel } from "shared/social/social-user-selectors"
import * as descriptionSelectors from "shared/state_management/selectors"
import * as customFieldSelectors from "shared/customFields/selectors"
import { SEAL_AVATAR_PATH, OFFICIAL_ISSUE_STAT_FIELD, SESSION_STAT_FIELD } from "shared/imports/sharedConstants"

import {
    checkPermission,
    checkRegionsWithinRegions,
    isNotAbleToViewSensitiveFields,
} from "shared/imports/permissionFunctions"
import { internationalRegions } from "shared/imports/regionConstants"
import { readFromCache, selectCache } from "shared/api-caching/selectors"
import * as socialUserSelectors from "shared/social/social-user-selectors"

import { DTV_STYLE_CONSTANTS } from "shared/profiles/constants/constants"
import { TEXT_URL_DELIMITER } from "app/static/frontend/profiles-new/constants"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { OFFICIAL_SECTION_TITLES } from "shared/profiles/official/constants"

export const selectProfileSlice = (state) => state.officialProfile

export const selectProfileInstance = createProfileInstanceSelector({
    selector: selectProfileSlice,
    key: "members",
})

export const makeProfileSelector = makeImmutableSliceSelector(selectProfileInstance)

export const selectProfile = makeProfileSelector("profile")
export const selectDistrictStats = makeProfileSelector("districtStats")

export const selectMemberImmutable = makeProfileSelector("member", fromJS({}))
/** @type {(state: any, props?: any) => any} */
export const selectMember = createSelector(selectMemberImmutable, (member) =>
    member && member.toJS ? member.toJS() : member,
)
export const selectPersonBiography = makeProfileSelector("personBiography")
export const selectIsLoadingMemberData = makeProfileSelector("isLoadingMember")
export const selectIsLoadingBiography = makeProfileSelector("isLoadingBiography")

export const selectIsLoadingRelationshipStats = makeProfileSelector("isLoadingRelationshipStats")
export const selectIsLoadingAllStats = makeProfileSelector("isLoadingAllStats")
export const selectIsLoadingIssueStats = makeProfileSelector("isLoadingIssueStats")

// new stats
export const selectSelectedStatIssue = makeProfileSelector(OFFICIAL_ISSUE_STAT_FIELD)
export const selectSelectedStatSession = makeProfileSelector(SESSION_STAT_FIELD)

/*
    This function is used to determine which filters should be passed when
    calling the loadStats function. This is called whenever a filter is
    added / modified in the store. It will check all of the potential filters
    that can be applid and conditionally add them to a filterObject if they
    exist
*/
export const selectExtraFilters = createSelector(
    selectSelectedStatSession,
    selectSelectedStatIssue,
    (session, issue) => ({
        ...(session ? { [SESSION_STAT_FIELD]: session } : {}),
        ...(issue ? { [OFFICIAL_ISSUE_STAT_FIELD]: issue } : {}),
    }),
)

export const selectRelationshipStatsObj = makeProfileSelector("relationshipStats")
export const selectAllStatsObj = makeProfileSelector("allStats")
export const selectIssueStatsObj = makeProfileSelector("issueStats")

export const selectRelationshipStatValues = filterStatsFromResponse(selectRelationshipStatsObj)
export const selectIssueStatValues = filterStatsFromResponse(selectIssueStatsObj)

export const selectPersonCache = createSelector(
    selectRelationshipStatsObj,
    (relationshipStats) => relationshipStats && relationshipStats.person_cache,
)

export const selectIssueCache = createSelector(
    selectIssueStatsObj,
    (issueStats) => issueStats && issueStats.issue_cache,
)

export const isLoadingOfficialProfile = createSelector(
    selectIsLoadingMemberData,
    selectIsLoadingBiography,
    customFieldSelectors.selectIsLoading,
    (loadingMemData, loadingBio, loadingCustomFields) => loadingMemData || loadingBio || loadingCustomFields,
)

export const selectCurrentBio = makeProfileSelector("currentBio")

export const selectIsPDF = createSelector(selectCurrentBio, (currentBio) => {
    const pdfValues = [
        DjangIO.app.person.types.PersonBiographyType.eu_cv.display_text,
        DjangIO.app.person.types.PersonBiographyType.declaration_of_interest.display_text,
    ]
    return pdfValues.includes(currentBio)
})

const makeProfileSliceSelector = makeObjSliceSelector(selectProfile)

// custom official related selectors
const makeSelectMemberAttribute = makeObjSliceSelector(selectMember)
export const selectMemberOrganization = makeSelectMemberAttribute("organization")
export const selectMemberUser = makeSelectMemberAttribute("user")
export const selectPacOrganizationsAttribute = makeSelectMemberAttribute("pac_organizations")
export const isCustomOfficial = createSelector(selectMemberOrganization, selectMemberUser, (org, user) => {
    return !!(org || user)
})

// district tab selectors
export const selectDistrictData = makeProfileSliceSelector("district_data")

export const selectBoxDescriptions = makeProfileSliceSelector("member_box_descriptions")
export const selectCustomData = makeProfileSliceSelector("custom_data")

export const makeBioSelector = makeImmutableSliceSelector(selectPersonBiography)

export const selectPhRoleData = makeBioSelector("_ph_role_data")

export const selectPersonBio = makeBioSelector("quorum_biography")
export const selectKwBio = makeBioSelector("kw_biography")
export const selectAlmanacBio = makeBioSelector("almanac_biography")
export const selectEuBio = makeBioSelector("eu_biography")
export const selectIeBio = createSelector(makeBioSelector("ie_biography"), (ieBio) => ieBio && lineBreaks(ieBio))
export const selectEuResponsibilities = makeBioSelector("eu_responsibilities")
export const selectEuCV = makeBioSelector("bio_pdf_url")
export const selectEuCVText = makeBioSelector("bio_pdf_fts_text")
export const selectDeclarationOfInterestsPDFUrl = makeBioSelector("declaration_of_interests_pdf_url")

export const selectSpouse = makeBioSelector("spouse")
export const selectFamily = makeBioSelector("family")
export const selectEthnicityEnum = makeBioSelector("ethnicity")
export const selectEthnicity = createSelector(
    selectEthnicityEnum,
    (ethnicity) => ethnicity && DjangIO.app.person.types.Ethnicity.by_value(ethnicity).label,
)
export const selectReligionEnum = makeBioSelector("religion")
export const selectReligion = createSelector(
    selectReligionEnum,
    (religion) => religion && DjangIO.app.person.types.Religion.by_value(religion).label,
)
export const selectResidence = makeBioSelector("residence")

export const selectPrevIndustry1 = makeBioSelector("previousindustry1")
export const selectPrevIndustry2 = makeBioSelector("previousindustry2")

export const selectPhCurrentRoles = createSelector(selectPhRoleData, (roleData) =>
    roleData?.filter((role) => role.get("current")),
)

export const selectShouldShowCurrentRoles = createSelector(
    selectPhCurrentRoles,
    // https://quorumanalytics.slack.com/archives/CKTE1LANA/p1587573307012200?thread_ts=1585948704.000900&cid=CKTE1LANA
    // if there is only one current role, it's the role the profile is for,
    // so we don't need to show it
    (currentRoles) => currentRoles,
)

export const selectPhPreviousExperience = createSelector(selectPhRoleData, (roleData) => {
    // https://quorumanalytics.slack.com/archives/CKTE1LANA/p1587573307012200?thread_ts=1585948704.000900&cid=CKTE1LANA
    const previousRoles =
        roleData &&
        roleData.filter(
            (role) =>
                // only include former (not current) roles
                !role.get("current") &&
                // exclude roles with sources that indicate they are education experiences
                ![
                    DjangIO.app.person.newtypes.PersonBiographyRoleDataSource.kw_cm_tblpeopleeducation.value,
                    DjangIO.app.person.newtypes.PersonBiographyRoleDataSource.kw_gov_tblpeopleeducation.value,
                    DjangIO.app.person.newtypes.PersonBiographyRoleDataSource.lc_education.value,
                ].includes(role.get("source")),
        )

    return previousRoles
})

export const selectPacOrganization = createSelector(selectPacOrganizationsAttribute, (pacOrgs) => {
    const pacOrganizations =
        pacOrgs &&
        pacOrgs.map((pacOrg) => ({
            text: pacOrg.name,
            id: pacOrg.id,
        }))

    return pacOrganizations
})

export const selectPhEducation = createSelector(selectPhRoleData, (roleData) => {
    // https://quorumanalytics.slack.com/archives/CKTE1LANA/p1587573307012200?thread_ts=1585948704.000900&cid=CKTE1LANA
    const educationRoles =
        roleData &&
        roleData.filter((role) =>
            // include roles with sources that indicate they are education experiences
            isFeatureEnabled("ff_lc_kw_failover")
                ? DjangIO.app.person.newtypes.PersonBiographyRoleDataSource.lc_education.value === role.get("source")
                : [
                      DjangIO.app.person.newtypes.PersonBiographyRoleDataSource.kw_cm_tblpeopleeducation.value,
                      DjangIO.app.person.newtypes.PersonBiographyRoleDataSource.kw_gov_tblpeopleeducation.value,
                  ].includes(role.get("source")),
        )

    return educationRoles
})

// test dis
export const selectPrevIndustry = createSelector([selectPrevIndustry1, selectPrevIndustry2], (prevInd1, prevInd2) => {
    const prevInd1Label = prevInd1 && DjangIO.app.person.types.Occupations.by_value(prevInd1).label
    const prevInd2Label = prevInd2 && DjangIO.app.person.types.Occupations.by_value(prevInd2).label
    return [prevInd1Label, prevInd2Label].filter((i) => i).join(", ")
})

const makeEducationSelector = (key) =>
    createSelector(
        [
            selectPersonBiography,
            isCustomOfficial,
            makeBioSelector(`education${key}`),
            makeBioSelector(`degreedate${key}`),
            makeBioSelector(`degree${key}`),
        ],
        (bio, isCustomOfficial, education, degreedate, degree) => {
            if (isCustomOfficial) {
                return {
                    school: education,
                    date: degreedate,
                    degree,
                }
            }

            if (bio) {
                const school = bio.get(`education${key}`)
                const date = bio.get(`degreedate${key}`)
                const degree = bio.get(`degree${key}`)
                return { school, date, degree }
            }
            return undefined
        },
    )

export const selectEducation1 = makeEducationSelector("1")
export const selectEducation2 = makeEducationSelector("2")
export const selectEducation3 = makeEducationSelector("3")

const isValidEd = ({ school }) => school

export const selectEducation = createSelector([selectEducation1, selectEducation2, selectEducation3], (ed1, ed2, ed3) =>
    [ed1, ed2, ed3].filter((ed) => ed && isValidEd(ed)),
)

export const selectCustomOfficialEducationSection = ({
    sectionComponent,
    educationDataSelector,
    title,
    id,
    gridWidth = 3,
    tooltipText,
}) =>
    createSelector(educationDataSelector, (education) => {
        const educationData = education.map((ed) => ({
            title: ed.degree,
            subtitle: ed.date,
            displayValue: ed.school,
            style: DTV_STYLE_CONSTANTS.ELEMENT,
        }))
        return (
            education &&
            education.length && {
                title,
                data: educationData,
                id,
                gridWidth,
                sectionComponent,
                tooltipText,
                collapsible: true,
                collapsedGridRows: 2,
            }
        )
    })

const makePrevExperienceSelector = (key) =>
    createSelector(selectPersonBiography, (bio) => {
        if (bio) {
            const prevExpType = bio.get(`previousrole${key}type`)
            const prevExpDate = bio.get(`previousrole${key}dates`)
            return { prevExpType, prevExpDate }
        }
        return undefined
    })

const isValidPrevExp = ({ prevExpType, prevExpDate }) => prevExpType && prevExpDate

export const selectPrevExperience = createSelector(
    [makePrevExperienceSelector("1"), makePrevExperienceSelector("2")],
    (prevExp1, prevExp2) => [prevExp1, prevExp2].filter((prevExp) => prevExp && isValidPrevExp(prevExp)),
)

export const makeMiltExperienceSelector = (key) =>
    createSelector(selectPersonBiography, (bio) => {
        if (bio) {
            const date = bio.get(`military${key}dates`)
            const branch = bio.get(`militarybranch${key}`)
            const role = bio.get(`militaryrole${key}`)
            return { date, branch, role }
        }
        return undefined
    })

const isValidMiltExp = ({ date, branch }) => date && branch

export const selectMiltExperience = createSelector(
    [makeMiltExperienceSelector("1"), makeMiltExperienceSelector("2")],
    (miltExp1, miltExp2) => [miltExp1, miltExp2].filter((miltExp) => miltExp && isValidMiltExp(miltExp)),
)

export const selectFormattedMiltExperience = createSelector(selectMiltExperience, (miltExp) =>
    !miltExp.length
        ? []
        : miltExp.reduce(
              (acc, { date, branch, role }) =>
                  acc.concat({
                      title: `Military Service ${date}`,
                      value: `${branch}${branch && role ? ", " : ""}${role || ""}`,
                  }),
              [],
          ),
)

export const selectRecentMiltExperience = createSelector(makeMiltExperienceSelector("1"), (miltExp) =>
    miltExp && isValidMiltExp(miltExp) ? [miltExp] : [],
)

export const selectFormattedRecentMiltExperience = createSelector(selectRecentMiltExperience, (recentMiltExp) =>
    !recentMiltExp.length
        ? [
              {
                  title: `Past Military Service`,
                  value: `None`,
              },
          ]
        : recentMiltExp.map((service) => ({
              title: `Past Military Service`,
              value:
                  `${service.branch}` +
                  `${service.branch && service.role ? ", " : ""}` +
                  `${service.role ? service.role : ""}` +
                  `, ${service.date}`,
          })),
)

export const selectPreviousMiltExperienceData = (isDLC) =>
    createSelector(
        [selectFormattedMiltExperience, selectFormattedRecentMiltExperience],
        (formattedMiltExp, formattedRecentMiltExp) => (!isDLC ? formattedMiltExp : formattedRecentMiltExp),
    )

// mobile selectors start here
const makeSelectMemberImmutableAttribute = makeImmutableSliceSelector(selectMemberImmutable)
export const selectRoleData = makeSelectMemberAttribute("most_recent_role", {})

const makeSelectRole = makeObjSliceSelector(selectRoleData)
export const selectNextElection = makeSelectRole("reelectionyear")
export const selectAddress = makeSelectRole("address")
// we just want the year here from a date like 2018-01-01
export const selectDateElected = createSelector(
    makeBioSelector("dateelected"),
    (electedString) => electedString && electedString.substring(0, 4),
)

export const selectFirstElectionDate = createSelector(
    makeBioSelector("first_election_date"),
    (firstElectedString) => firstElectedString && firstElectedString.substring(0, 4),
)

export const selectedAssumedOfficeDate = createSelector(
    makeBioSelector("assumed_office_date"),
    (assumedOfficeDate) => assumedOfficeDate && assumedOfficeDate.substring(0, 4),
)

export const selectMostRecentRegion = makeSelectMemberAttribute("most_recent_region")
export const selectMostRecentRoleType = makeSelectMemberAttribute("most_recent_role_type")
export const isEuPerson = createSelector(
    selectMostRecentRegion,
    (region) => region === DjangIO.app.models.Region.eu.value,
)

export const selectMostRecentRegions = makeSelectMemberAttribute("most_recent_regions")
export const selectIsInternationalPerson = createSelector(
    selectMostRecentRegions,
    (regions) => Boolean(regions) && checkRegionsWithinRegions(regions, internationalRegions),
)

export const selectMemberPrefix = createRegionHeaderTitlePrefixSelector(selectMostRecentRegion)

export const selectParties = makeSelectMemberAttribute("party_string")
export const selectPartyStrings = createSelector(
    selectParties,
    (parties) =>
        parties &&
        Object.values(JSON.parse(parties))
            .map((value) => value)
            .join(", "),
)

export const selectCurrentMajorRoleTypes = makeSelectMemberAttribute("_ph_dn_current_major_role_types")

export const selectCurrentMajorRoleTypeStrings = createSelector(
    selectCurrentMajorRoleTypes,
    (currentMajorRoleTypes) => {
        // currentMajorRoleTypes is an array of ints corresponding to the MajorRoleType enum
        if (currentMajorRoleTypes && currentMajorRoleTypes.length) {
            return deduplicatedObjectArrayToString({
                enumeration: DjangIO.app.person.newtypes.MajorRoleType.region_supported_items(),
                values: currentMajorRoleTypes,
                attribute: "title",
            })
        }
    },
)

export const selectMemberImgUrl = makeSelectMemberAttribute("image_url")
// Show Election Status for Profiles only if it is not an Ongoing Person
export const selectElectionStatus = createSelector(makeSelectMemberAttribute("status"), (status) => {
    const statusLabel = status && DjangIO.app.person.types.PersonStatus.by_value(status).label
    return statusLabel !== DjangIO.app.person.types.PersonStatus.active.label ? statusLabel : undefined
})
// Show Election Status for all DLC Profiles
export const selectElectionStatusForDLC = createSelector(
    makeSelectMemberAttribute("status"),
    (status) => status && DjangIO.app.person.types.PersonStatus.by_value(status).label,
)

export const selectMostRecentPersonType = makeSelectMemberAttribute("most_recent_person_type")
export const selectMostRecentExecutiveType = createSelector(
    selectMember,
    // makeObjSliceSelector checks the truthyness of obj[key], so if you have an enum with value 0,
    // it will return undefined rather than 0
    (member) => member && (member.most_recent_role.is_executive ? member.most_recent_role : undefined),
)
export const selectCookStuff = makeProfileSliceSelector("cook_stuff")
export const selectCookPVI = createSelector(
    makeSelectMemberAttribute("cook_pvi"),
    selectMostRecentPersonType,
    (cookVal, personType) => personType !== DjangIO.app.person.types.PersonType.staffer.value && cookVal,
)
export const selectShowinQuicksearch = makeSelectMemberAttribute("show_in_quicksearch")

export const firstname = makeSelectMemberAttribute("firstname")
export const lastname = makeSelectMemberAttribute("lastname")
/** @type {(state: any, props?: any) => any} */
export const selectIsStaffer = createSelector(
    selectMostRecentPersonType,
    (personType) => personType === DjangIO.app.person.types.PersonType.staffer.value,
)

export const alertName = createSelector(firstname, lastname, (firstName, lastName) => `${firstName} ${lastName}`)

/** @type {(state: any, props?: any) => any} */
export const selectWebsite = createSelector(selectRoleData, (roleData) => roleData && roleData.website)

export const shouldRenderDownloadButton = createSelector(
    selectShowinQuicksearch,
    selectMostRecentRoleType,
    (showInQuicksearch, roleType) =>
        (checkPermission(DjangIO.app.models.PermissionType.legislative_profiles) ||
            checkPermission(DjangIO.app.models.PermissionType.custom_data)) &&
        showInQuicksearch &&
        roleType &&
        DjangIO.app.person.types.RoleType.by_value(roleType).profile_download,
)

export const selectImgUrl = createSelector(
    [selectMemberImgUrl, selectMostRecentPersonType, selectMostRecentRegion],
    (memberImgUrl, mostRecentPersonType, mostRecentRegion) => {
        // yes this is ugly
        const url =
            mostRecentPersonType === DjangIO.app.person.types.PersonType.staffer.value
                ? `${SEAL_AVATAR_PATH}${DjangIO.app.models.Region.by_value(mostRecentRegion).region_name.replace(
                      " ",
                      "",
                  )}.png`
                : memberImgUrl

        return generateStaticUrl(url)
    },
)
export const selectMemberName = makeSelectMemberAttribute("name")
export const selectMostRecentParty = makeSelectMemberAttribute("most_recent_party")
export const selectCurrent = makeSelectMemberAttribute("current")
export const selectTitle = makeSelectMemberAttribute("title")
export const selectPublicOrgDescriptor = makeSelectMemberAttribute("public_organization_descriptor")
export const selectMemberId = (state, props) => (props && props.profileId) || (props.params && props.params.profileId)
export const selectMemberResourceURI = makeSelectMemberAttribute("resource_uri")
export const selectPrimaryOrg = makeSelectMemberAttribute("primary_organization")

export const transactions = makeSelectMemberAttribute("transactions")

export const makePrimaryOrgSelect = makeObjSliceSelector(selectPrimaryOrg)
export const selectPrimaryOrgName = makePrimaryOrgSelect("name")
export const selectPrimaryOrgId = makePrimaryOrgSelect("id")
export const selectMemberIsFederalLegislator = createSelector(selectMostRecentRoleType, (officialRoleType) => {
    const { RoleType } = DjangIO.app.person.types

    return [RoleType.senator.value, RoleType.representative.value].includes(officialRoleType)
})

export const selectPartyColor = createSelector(selectMostRecentParty, (party) => {
    const partyEnum = DjangIO.app.person.types.Party.by_value(party)
    return partyEnum && partyEnum.color
})

export const shouldRenderEditButton = createSelector(isCustomOfficial, (isCustom) => Userdata.is_admin || !isCustom)

export const headerTitle = createSelector(
    selectCurrent,
    selectMostRecentRoleType,
    selectPrimaryOrgName,
    selectMostRecentPersonType,
    selectMemberName,
    selectMemberPrefix,
    (isCurrent, roleType, primaryOrg, personType, name) =>
        `${name}` +
        // add primary org for local people
        `${
            DjangIO.app.person.types.RoleType.filter_groups
                .find((fg) => fg.title === "Local People")
                .values.includes(roleType) && primaryOrg
                ? ` | ${primaryOrg}`
                : ""
        }`,
)

export const selectNoPrefixHeaderTitle = createSelector(headerTitle, selectMemberPrefix, (name, prefix) => {
    const hasPrefixInTitle = prefix && name.startsWith(prefix)
    const title = hasPrefixInTitle ? name.substring(prefix.length) : name
    return title
})

export const selectUncleanCommitteeString = createSelector(
    makeSelectMemberAttribute("committee_string"),
    (committeeString) => committeeString && committeeString.replace("House House", "House"),
)

export const selectMemberCommitteeString = createSelector(selectUncleanCommitteeString, (committeeString) =>
    committeeString ? cleanUpHtml(committeeString.toString()) : committeeString,
)

export const officialCustomDescription = descriptionSelectors.selectSpecificCustomDescription(
    DjangIO.app.models.QuorumDataType.person.key,
)

const makeOfficialDescriptionSelect =
    descriptionSelectors.makeSelectFieldFromCustomDescription(officialCustomDescription)

/** @type {(state: any, props?: any) => any} */
export const selectCustomDescriptionText = makeOfficialDescriptionSelect("text")
export const selectCustomDescriptionId = makeOfficialDescriptionSelect("id")
export const selectCurrentTab = makeOfficialDescriptionSelect("currentTab")
export const selectCustomBioPreferredName = makeOfficialDescriptionSelect("preferred_name")
export const selectCustomBioPersonalEmail = makeOfficialDescriptionSelect("personal_email")
export const selectCustomBioPersonalPhoneNumber = makeOfficialDescriptionSelect("personal_phone_number")
export const selectCustomBioIsUnsubscribed = makeOfficialDescriptionSelect("is_unsubscribed")
export const selectCustomBioShouldUsePersonalEmail = makeOfficialDescriptionSelect("should_use_personal_email")
export const selectCustomDescriptionPreferredName = makeOfficialDescriptionSelect("preferred_name")
export const selectCustomDescriptionCustomImageUrl = makeOfficialDescriptionSelect("custom_image_s3_url")
export const selectCustomDescriptionPersonalEmail = makeOfficialDescriptionSelect("personal_email")
export const selectCustomDescriptionPersonalPhoneNumber = makeOfficialDescriptionSelect("personal_phone_number")
export const selectCustomDescriptionIsUnsubscribed = makeOfficialDescriptionSelect("is_unsubscribed")
export const selectCustomDescriptionShouldUsePersonalEmail = makeOfficialDescriptionSelect("should_use_personal_email")
export const selectCustomTagsNotJS = makeOfficialDescriptionSelect("tag_dict")
export const selectCustomTags = createToJsSelector(selectCustomTagsNotJS, {})
export const selectCustomDescriptionIsSubmitting = makeOfficialDescriptionSelect("isSubmitting")
/** @type {(state: any, props?: any) => any} */
export const selectCustomDescriptionIsLoading = makeOfficialDescriptionSelect("isLoading")

export const selectIsLoadingMember = createSelector(
    selectIsLoadingMemberData,
    selectIsLoadingBiography,
    customFieldSelectors.selectIsLoading,
    (loadingMember, loadingBio, loadingCustomFields) => loadingMember || loadingBio || loadingCustomFields,
)

// this should eventually be decoupled from this file
export const selectForm = (state) => state.form

export const selectOfficialForm = createSelector(selectForm, (form) => form && form.get("officialForm"))

export const selectFormValues = createSelector(
    selectOfficialForm,
    (officialForm) => officialForm && officialForm.get("values"),
)

export const selectFormRegisteredFields = createSelector(
    selectOfficialForm,
    (officialForm) =>
        officialForm && officialForm.get("registeredFields") && officialForm.get("registeredFields").toJS(),
)

export const selectOfficialFormValues = createSelector(
    [selectCustomDescriptionId, selectCache, selectCustomTags, selectFormRegisteredFields, selectFormValues],
    (customDescriptionId, cache, previousTagDict, formRegisteredFields, allFormFieldsValues) => {
        if (customDescriptionId) {
            // If DataDescription between this official and the organization exists

            const url = DjangIO.app.userdata.models.DataDescription.objects.get(customDescriptionId).buildEndpoint()

            const {
                preferred_name,
                personal_email,
                personal_phone_number,
                is_unsubscribed,
                should_use_personal_email,
                tag_dict,
            } = readFromCache(cache, url)

            if (Object.keys(tag_dict).length > 0) {
                Object.keys(tag_dict).forEach((key) => {
                    const textURLSlug = `${key}_url`
                    if (formRegisteredFields && formRegisteredFields[textURLSlug]) {
                        const textValue = allFormFieldsValues.toJS()[key] || tag_dict[key]
                        const textURLValue = allFormFieldsValues.toJS()[textURLSlug]
                        tag_dict[key] = textValue
                        tag_dict[textURLSlug] = textURLValue
                        if (textValue.includes(TEXT_URL_DELIMITER)) {
                            const substringURL = textValue.slice(
                                textValue.indexOf(TEXT_URL_DELIMITER) + TEXT_URL_DELIMITER.length,
                                textValue.length,
                            )
                            const substringText = textValue.slice(0, textValue.indexOf(TEXT_URL_DELIMITER))
                            tag_dict[textURLSlug] = substringURL
                            tag_dict[key] = substringText
                        }
                        if (textURLValue && textValue.endsWith(`${TEXT_URL_DELIMITER}${textURLValue})`)) {
                            tag_dict[key] = textValue.slice(`${TEXT_URL_DELIMITER}${textURLValue})`.length)
                        }
                    }
                })
            }

            return {
                preferred_name,
                personal_email,
                personal_phone_number,
                is_unsubscribed,
                should_use_personal_email,
                ...tag_dict,
            }
        }

        if (Object.keys(previousTagDict).length > 0) {
            Object.keys(previousTagDict).forEach((key) => {
                const textURLSlug = `${key}_url`
                if (formRegisteredFields && formRegisteredFields[textURLSlug]) {
                    const textValue = allFormFieldsValues.toJS()[key] || previousTagDict[key]
                    const textURLValue = allFormFieldsValues.toJS()[textURLSlug]
                    previousTagDict[key] = textValue
                    previousTagDict[textURLSlug] = textURLValue
                    if (textValue.includes(TEXT_URL_DELIMITER)) {
                        const substringURL = textValue.slice(
                            textValue.indexOf(TEXT_URL_DELIMITER) + TEXT_URL_DELIMITER.length,
                            textValue.length,
                        )
                        const substringText = textValue.slice(0, textValue.indexOf(TEXT_URL_DELIMITER))
                        previousTagDict[textURLSlug] = substringURL
                        previousTagDict[key] = substringText
                    }
                    if (textURLValue && textValue.endsWith(`${TEXT_URL_DELIMITER}${textURLValue})`)) {
                        previousTagDict[key] = textValue.slice(`${TEXT_URL_DELIMITER}${textURLValue})`.length)
                    }
                }
            })
        }
        return {
            preferred_name: undefined,
            personal_email: undefined,
            personal_phone_number: undefined,
            is_unsubscribed: undefined,
            should_use_personal_email: undefined,
            ...previousTagDict,
        }
    },
)
/*

The following selectors create the data object necessary to render the Profile using
React-Native's SectionList component. The SectionList renders a ListItem component
(components/Lists/LisItem) for each of the items in the section's array.
The data is structed like:

sections = [
    // sectionObj used to render each section in the SectionList
    {
        title: sectionTitle1,
        // dataObj used to render the ListItem Component
        data: [
            { id11, title11, subtitle11, onPressFunct11, iconName11},
            { id12, title12, subtitle12, onPressFunct12, iconName12},
            { id13, title13, subtitle13, onPressFunct13, iconName13},
            ...
        ],
        id: 1
    },
    {
        title: sectionTitle2,
        // dataObj used to render the ListItem Component
        data: [
            { id21, title21, subtitle21, onPressFunct21, iconName21 },
            ...
        ],
        id: 2
    }
]

each sectionObj is then pushed to a sections object that gets returned.

*/

export const selectCustomFields = createSelector(
    [selectMember, (state, props) => [state, props]],
    (member, [state, props]) => {
        const selectorFunc =
            member.most_recent_person_type === DjangIO.app.person.types.PersonType.staffer.value
                ? customFieldSelectors.selectCustomFieldNameDictForProfile(
                      DjangIO.app.models.QuorumDataType.staffer.value,
                  )
                : customFieldSelectors.selectCustomFieldNameDictForProfile(
                      DjangIO.app.models.QuorumDataType.person.value,
                  )
        return selectorFunc(state, props)
    },
)

export const selectCustomDataFields = ({ sectionComponent, selectorFunctions }) =>
    createSelector(
        [
            selectCustomDescriptionPreferredName,
            selectCustomDescriptionPersonalEmail,
            selectCustomDescriptionPersonalPhoneNumber,
            selectCustomDescriptionIsUnsubscribed,
            selectCustomDescriptionShouldUsePersonalEmail,
            selectCustomTags,
            selectCustomFields,
            selectMemberIsFederalLegislator,
        ],
        (
            preferredName,
            personalEmail,
            personalPhoneNumber,
            isUnsubscribed,
            shouldUsePersonalEmail,
            tagDict,
            customFields,
            isFederalLegislator,
        ) => {
            const customData = [
                {
                    title: "Preferred Name",
                    value: preferredName,
                    width: 3,
                },
                ...(!isNotAbleToViewSensitiveFields()
                    ? [
                          {
                              title: "Personal Email",
                              value: personalEmail,
                              width: 3,
                          },
                          {
                              title: "Personal Phone Number",
                              value: personalPhoneNumber,
                              onPress:
                                  selectorFunctions &&
                                  selectorFunctions({ phoneNumber: personalPhoneNumber }).phoneNumber,
                              icon: "phone",
                              width: 3,
                          },
                      ]
                    : []),
                {
                    title: "Unsubscribed from Outbox",
                    value: !isFederalLegislator && (isUnsubscribed ? "Yes" : "No"),
                    width: 3,
                },
                ...(!isNotAbleToViewSensitiveFields()
                    ? [
                          {
                              title: "Use Personal Email in Outbox",
                              value: !isFederalLegislator && (shouldUsePersonalEmail ? "Yes" : "No"),
                              width: 3,
                          },
                      ]
                    : []),
                ...createCustomFieldObjects({ customFields, tagDict }).map((dataObj) => Object.assign(dataObj)),
            ]
            const customDataObj = createSectionObject({
                title: OFFICIAL_SECTION_TITLES.customFields,
                data: createDataSections(customData),
                id: "officialCustomData",
                sectionComponent,
                canEditSection: true,
                customData: true,
            })
            return customDataObj
        },
    )

export const selectBioData = ({ selectorFunctions, sectionComponent }) =>
    createSelector(
        [
            selectPersonBio,
            selectAlmanacBio,
            selectKwBio,
            selectEuBio,
            selectIeBio,
            selectEuResponsibilities,
            selectEuCV,
            selectDeclarationOfInterestsPDFUrl,
        ],
        (quorumBiography, almanacBiography, kwBiography, euBio, ieBio, euResponsibilities, euCVLink, interestsUrl) => {
            const bioData = [
                {
                    title: DjangIO.app.person.types.PersonBiographyType.almanac_biography.display_text,
                    value: almanacBiography,
                    onPress: selectorFunctions && selectorFunctions({ almanacBiography }).almanacBiography,
                    icon: "angle-right",
                    hideTitle: false,
                    hideValue: true,
                },
                {
                    title: DjangIO.app.person.types.PersonBiographyType.quorum_biography.display_text,
                    value: quorumBiography || euBio || ieBio,
                    onPress: selectorFunctions && selectorFunctions({ quorumBiography }).quorumBiography,
                    icon: "angle-right",
                    hideTitle: false,
                    hideValue: true,
                },
                {
                    title: DjangIO.app.person.types.PersonBiographyType.kw_biography.display_text,
                    value: kwBiography,
                    onPress: selectorFunctions && selectorFunctions({ kwBiography }).kwBiography,
                    icon: "angle-right",
                    hideTitle: false,
                    hideValue: true,
                },
                {
                    title: DjangIO.app.person.types.PersonBiographyType.eu_responsibilities.display_text,
                    value: euResponsibilities,
                    onPress: selectorFunctions && selectorFunctions({ euResponsibilities }).euResponsibilities,
                    icon: "angle-right",
                    hideTitle: !euBio,
                    hideValue: !!euBio,
                },
                {
                    title: DjangIO.app.person.types.PersonBiographyType.eu_cv.display_text,
                    value: euCVLink,
                    onPress: selectorFunctions && selectorFunctions({ euCVLink }).euCVLink,
                    icon: "globe",
                },
                {
                    title: DjangIO.app.person.types.PersonBiographyType.declaration_of_interest.display_text,
                    value: interestsUrl,
                    onPress: selectorFunctions && selectorFunctions({ interestsUrl }).interestsUrl,
                    icon: "globe",
                },
            ]

            const bioObj = createSectionObject({
                title: OFFICIAL_SECTION_TITLES.bioData,
                data: createDataSections(bioData),
                id: "officialBioSection",
                sectionComponent,
            })
            return bioObj
        },
    )

export const selectRegionAvatarUrl = createSelector(selectMember, (memberData) =>
    getRegionImageUrl(memberData && memberData.most_recent_region),
)

export const selectContactCardData = ({ sectionComponent }) =>
    createSelector(
        selectMember,
        selectRoleData,
        selectUncleanCommitteeString,
        selectPrimaryOrgName,
        selectPrimaryOrgId,
        selectPartyColor,
        selectRegionAvatarUrl,
        (member, roleData, committeeString, primaryOrgName, primaryOrgId, partyColor, regionAvatarUrl) => {
            const contactData = member
                ? [
                      {
                          circleColor: partyColor,
                          detailItems: createContactCardDetailItems({
                              email: member.email,
                              phoneNumber: roleData.phone,
                              address: roleData.address,
                          }),
                          infoItems: [
                              {
                                  isHtml: !!committeeString,
                                  text: committeeString,
                                  textType: "primary",
                              },
                              {
                                  href:
                                      primaryOrgName &&
                                      primaryOrgId &&
                                      `${DjangIO.app.models.QuorumDataType.public_organization.profile}${primaryOrgId}`,
                                  label: "Org",
                                  text: primaryOrgName,
                                  textType: "primary",
                              },
                          ],
                          icon: "user",
                          // use either the image url if we have one, or the region seal for staffers if we don't
                          imageUrl:
                              member.image_url ||
                              (member.most_recent_role_type === DjangIO.app.person.types.RoleType.staffer.value &&
                                  regionAvatarUrl),
                          isV2Image: true,
                          name: member.name,
                      },
                  ]
                : []

            const contactCardObj = createSectionObject({
                title: OFFICIAL_SECTION_TITLES.contactCard,
                data: contactData,
                id: "officialContactCard",
                sectionComponent,
            })

            return contactCardObj
        },
    )

export const selectContactData = createSelector(
    selectMember,
    selectRoleData,
    (person, roleData) =>
        person && {
            familyName: person.firstname,
            givenName: person.lastname,
            emailAddresses: [
                {
                    label: "work",
                    email: person.email,
                },
            ],
            phoneNumbers: [
                {
                    label: "work",
                    number: roleData.phone,
                },
            ],
            company: person.primary_organization_name,
            postalAddresses: [
                {
                    street: roleData.address,
                    city: "",
                    state: "",
                    region: "",
                    postCode: "",
                    country: "",
                    label: "work",
                },
            ],
        },
)
export const selectWikiPage = makeSelectMemberAttribute("wikipedia_url")
export const isInternational = createSelector(
    selectMostRecentRegion,
    (region) =>
        region &&
        DjangIO.app.models.Region.by_value(region) &&
        DjangIO.app.models.Region.by_value(region).is_international_region,
)

export const selectPoliticalCommittees = createExtraFieldSelector({
    key: "political_committees",
    objectSelector: makeSelectMemberImmutableAttribute,
})

export const selectPoliticalCommitteesList = createSelector(
    selectPoliticalCommittees,
    (committees) => committees && committees.toJS(),
)

export const selectCommitteeSection = ({ sectionComponent }) =>
    createSelector(selectPoliticalCommitteesList, (committees) =>
        createSectionObject({
            title: OFFICIAL_SECTION_TITLES.politicalCommittees,
            data: [{ committees }],
            sectionComponent,
        }),
    )

export const selectOfficePhoneNumbers = (selectorFunctions) =>
    createSelector(
        createToJsSelector(
            createExtraFieldSelector({ key: "office_phones", objectSelector: makeSelectMemberImmutableAttribute }),
        ),
        (phoneNumbers) =>
            phoneNumbers
                ? phoneNumbers.map((numberObj) => ({
                      title: `${numberObj.city} Office Phone`,
                      value: numberObj.phone_number,
                      onPress:
                          selectorFunctions && selectorFunctions({ phoneNumber: numberObj.phone_number }).phoneNumber,
                      icon: "phone",
                      width: 3,
                  }))
                : [],
    )

export const selectOfficeInfoSection = ({ selectorFunctions, sectionComponent }) =>
    createSelector(
        selectOfficePhoneNumbers(selectorFunctions),
        selectMemberIsFederalLegislator,
        (officePhoneNumbers, isFederalOfficial) =>
            // ...(officePhoneNumbers && officePhoneNumbers.length ? officePhoneNumbers : {}),
            createSectionObject({
                // only members of congress should refer to these as "district offices"
                title: isFederalOfficial ? OFFICIAL_SECTION_TITLES.districtOffices : "Offices",
                data: createDataSections(officePhoneNumbers),
                id: "officialOfficeInfo",
                sectionComponent,
            }),
    )

export const selectOtherInfoSection = ({ selectorFunctions, sectionComponent }) =>
    createSelector(
        selectOfficePhoneNumbers(selectorFunctions),
        selectMemberIsFederalLegislator,
        (officePhoneNumbers, isFederalLegislator) => {
            // currently only shows office phone numbers for non-members of congress,
            // but other extraneous DTV details can be shown for the profile here
            // (for post-DS upgrade profiles)
            const data = [
                // only include office phone numbers here for non-members of congress
                ...(isFederalLegislator ? [] : officePhoneNumbers),
            ]

            return createSectionObject({
                title: "Other Information",
                data: createDataSections(data),
                id: "officialOtherInfo",
                sectionComponent,
            })
        },
    )

export const selectGeneralData = ({ isMobile, selectorFunctions, sectionComponent, isDLC }) =>
    createSelector(
        [
            selectMember,
            selectContactData,
            selectMiltExperience,
            selectAddress,
            selectRoleData,
            isEuPerson,
            selectIsInternationalPerson,
            selectPartyStrings,
            selectCurrentMajorRoleTypeStrings,
        ],
        (
            memberData,
            contactData,
            miltExp,
            address,
            roleData,
            isEuPerson,
            internationalPerson,
            partyStrings,
            majorRoleTypeStrings,
        ) => {
            const generalData = [
                // for staffer
                {
                    title: "Works For",
                    value:
                        memberData.person &&
                        memberData.most_recent_person_type === DjangIO.app.person.types.PersonType.staffer.value
                            ? memberData.person.name
                            : "",
                    onPress: selectorFunctions && selectorFunctions({ memberData }).member,
                    icon: "angle-right",
                },
                {
                    title: "Pronunciation",
                    value: memberData.pronunciation,
                },
                {
                    title: "Address",
                    value: address,
                    onPress: selectorFunctions && selectorFunctions({ address }).address,
                    icon: "map-marker",
                    width:
                        memberData.person &&
                        memberData.most_recent_person_type === DjangIO.app.person.types.PersonType.staffer.value
                            ? 12
                            : 6,
                },
                // only add the phone number in this section from the roleData for EU members
                // for federal / US officials, we add the phone numbers to the `District Offices`
                // section in selectOfficeInfoSection
                isEuPerson && {
                    title: "Phone Number",
                    value: roleData.phone,
                    onPress: selectorFunctions && selectorFunctions({ phoneNumber: roleData.phone }).phoneNumber,
                    icon: "phone",
                    width: 3,
                },
                // staffer only
                {
                    title: "Responsibilities",
                    width: 12,
                    value:
                        memberData.most_recent_person_type === DjangIO.app.person.types.PersonType.staffer.value
                            ? memberData.responsibilities_string
                            : "",
                },
                // for official only
                !!(
                    memberData.most_recent_person_type !== DjangIO.app.person.types.PersonType.staffer.value &&
                    ![
                        DjangIO.app.person.types.RoleType.senator.value,
                        DjangIO.app.person.types.RoleType.representative.value,
                        DjangIO.app.person.types.RoleType.state_senator.value,
                        DjangIO.app.person.types.RoleType.state_representative.value,
                        DjangIO.app.person.types.RoleType.state_councilmember.value,
                        DjangIO.app.person.types.RoleType.state_delegate.value,
                        DjangIO.app.person.types.RoleType.state_assemblymember.value,
                    ].includes(memberData.most_recent_role_type)
                ) && {
                    title: "Organization",
                    value: memberData.public_organization_descriptor,
                },
                // for staffer or non-federal officials
                (![
                    DjangIO.app.person.types.RoleType.senator.value,
                    DjangIO.app.person.types.RoleType.representative.value,
                ].includes(memberData.most_recent_role_type) ||
                    !!memberData.person) && {
                    title: "Email",
                    value: memberData.email,
                    onPress: selectorFunctions && selectorFunctions({ memberData }).email,
                    icon: "envelope-o",
                    isLink: true,
                    width: 6,
                },
                {
                    title: "Website",
                    value: roleData.website,
                    onPress: selectorFunctions && selectorFunctions({ roleData }).website,
                    icon: "globe",
                    isLink: true,
                    width: 3,
                },
                isDLC &&
                    isEuPerson && {
                        // should be eu/dlc only, should filter string
                        title: "Committee",
                        value: cleanUpHtml(memberData.committee_string),
                    },
                internationalPerson && {
                    title: "Party",
                    value: partyStrings,
                    onPress: selectorFunctions && partyStrings,
                    icon: "check-square-o",
                    width: 3,
                },
                internationalPerson && {
                    title: "Official Types",
                    value: majorRoleTypeStrings,
                    onPress: selectorFunctions && majorRoleTypeStrings,
                    icon: "fa-book",
                },
            ]

            generalData.push({
                value: "Download to Contacts",
                icon: "download",
                alwaysRender: true,
                hideTitle: true,
                onPress: selectorFunctions && selectorFunctions({ contactData }).downloadContact,
                isMobile: true,
            })

            const generalDataSections = createDataSections(
                !isMobile ? generalData.filter((dataObj) => dataObj && !dataObj.isMobile) : generalData,
            )

            return createSectionObject({
                title: "Contact Information",
                data: generalDataSections,
                id: "officialGeneralData",
                sectionComponent,
            })
        },
    )

export const selectBirthday = parseDateFromSelector(makeBioSelector("_ph_birthdate"))
export const selectAge = makeSelectMemberAttribute("age")
export const selectRegionRepresented = makeSelectMemberAttribute("region_represented")
export const selectSourceUrls = makeSelectMemberAttribute("source_urls")
export const selectMarital = makeBioSelector("marital")
export const selectBirthplace = makeBioSelector("birthplace")

export const selectIsLegislator = createSelector(selectMember, (member) => {
    if (!member) {
        return false
    }

    return member.most_recent_ph_meta_role_type === DjangIO.app.person.newtypes.MetaRoleType.legislative.value
})

export const selectIsExecutive = createSelector(selectMember, (member) => {
    if (!member) {
        return false
    }

    return member.most_recent_ph_meta_role_type === DjangIO.app.person.newtypes.MetaRoleType.executive.value
})

export const selectAboutData = ({ sectionComponent, selectorFunctions, isDLC }) =>
    createSelector(
        [
            selectNextElection,
            selectSpouse,
            selectFamily,
            selectEthnicity,
            selectReligion,
            selectResidence,
            selectBirthday,
            selectAge,
            selectMember,
            selectRegionRepresented,
            selectWikiPage,
            selectSourceUrls,
            selectMarital,
            selectBirthplace,
            selectElectionStatus,
            selectElectionStatusForDLC,
            selectFirstElectionDate,
            selectedAssumedOfficeDate,
        ],
        (
            nextElection,
            spouse,
            family,
            ethnicity,
            religion,
            residence,
            birthday,
            age,
            memberData,
            regionRepresented,
            wikiPage,
            sourceUrls,
            maritalStatus,
            birthplace,
            electionStatus,
            electionStatusForDLC,
            firstElectionDate,
            assumedOfficeDate,
        ) => {
            const aboutData = [
                ...(assumedOfficeDate
                    ? [
                          {
                              title: "Appointment Start Date",
                              value: assumedOfficeDate,
                              width: 3,
                          },
                      ]
                    : []),
                ...(firstElectionDate
                    ? [
                          {
                              title: "First Elected",
                              value: firstElectionDate,
                              width: 3,
                          },
                      ]
                    : []),
                {
                    title: "Next Election",
                    value: nextElection,
                    width: 3,
                },
                {
                    title: "Marital Status",
                    value: maritalStatus,
                    width: 3,
                },
                {
                    title: "Spouse",
                    value: spouse,
                    width: 3,
                },
                {
                    title: "Religion",
                    value: religion,
                    width: 3,
                },
                {
                    title: "Birthday",
                    value: birthday,
                    width: 3,
                },
                ...(!isFeatureEnabled("ff_lc_kw_failover")
                    ? [
                          {
                              title: "Family",
                              value: family,
                              width: 3,
                          },
                          {
                              title: "Ethnicity",
                              value: ethnicity,
                              width: 3,
                          },
                          {
                              title: "Birthplace",
                              value: birthplace,
                              width: 3,
                          },
                          {
                              title: "Residence",
                              value: residence,
                              width: 3,
                          },
                      ]
                    : []),
                {
                    title: "Age",
                    value: age,
                    width: 3,
                },
                {
                    title: "Region Represented",
                    value: regionRepresented,
                    width: 3,
                },
                {
                    title: "Election Status",
                    value: isDLC ? electionStatusForDLC : electionStatus,
                    width: 3,
                },
                {
                    title: "Wikipedia Page",
                    value: wikiPage,
                    onPress: selectorFunctions && selectorFunctions({ wikiPage }).wikiPage,
                    icon: "globe",
                    isLink: true,
                    width: 3,
                },
                {
                    title: "Term",
                    value: memberData.num_terms && `${ordinalSuffix(memberData.num_terms)} term`,
                },
                ...makeSourceUrlObjs(sourceUrls),
            ]

            const aboutObj = createSectionObject({
                title: OFFICIAL_SECTION_TITLES.about,
                data: createDataSections(aboutData),
                id: "officialAboutSection",
                sectionComponent,
            })

            return aboutObj
        },
    )

export const selectAdditionalInformationData = ({ sectionComponent }) =>
    createSelector(selectMember, (memberData) => {
        // Responsibilities is staffer only;
        // this section will not appear for other
        // officials until more information is added
        const infoData = [
            {
                title: "Responsibilities",
                width: 12,
                value:
                    memberData.most_recent_person_type === DjangIO.app.person.types.PersonType.staffer.value &&
                    memberData.responsibilities_string,
            },
        ]

        const infoObj = createSectionObject({
            title: "Additional Information",
            data: createDataSections(infoData),
            id: "officialAdditionalInfoSection",
            sectionComponent,
        })

        return infoObj
    })

export const selectPrevExperienceData = ({ sectionComponent, isDLC }) =>
    createSelector(
        [selectPrevExperience, selectPreviousMiltExperienceData(isDLC), selectPrevIndustry, selectEducation],
        (prevExp, miltExpData, prevIndustry, education) => {
            const prevExpData = [
                {
                    title: "Before Politics",
                    value: prevIndustry.length ? prevIndustry : undefined,
                },
                ...(!prevExp.length
                    ? []
                    : prevExp.reduce(
                          (acc, { prevExpType, prevExpDate }) =>
                              acc.concat({
                                  title: prevExpDate,
                                  value: prevExpType,
                              }),
                          [],
                      )),
                ...(!education.length
                    ? []
                    : education.reduce(
                          (acc, { school, date, degree }) =>
                              acc.concat({
                                  title: "Education",
                                  value: `${school}${degree ? ` ${degree}` : ""}${date ? ` ${date}` : ""}`,
                              }),
                          [],
                      )),
                ...miltExpData,
            ]

            return createSectionObject({
                title: "Previous Experience",
                data: createDataSections(prevExpData),
                id: "officialPreviousExperience",
                sectionComponent,
            })
        },
    )

/**
 * Takes a component and selector to create a selector for a section which displays
 * role data from the _ph_role_data field of the person. The input selector should
 * return an immutable list of role data objects
 *
 * @name selectPhSection
 * @function
 * @param {Component} sectionComponent  - the component to render the section data with
 * @param {Selector} phRoleDataSelector - a selector which selects roles to display from _ph_role_data
 * @param {String} title    - the title of the section
 * @param {String} id   - the id of the section
 * @param {Number} gridWidth    - the grid width to be included in the section data, for DTV grids
 *
 * @returns {Selector}  returns a selector which selects a section for displaying
 *                      personal hygiene experience data
 */
export const selectPhSection = ({ sectionComponent, phRoleDataSelector, title, id, gridWidth = 3, tooltipText }) =>
    createSelector(phRoleDataSelector, (phRoles) => {
        const roleSectionData = !phRoles
            ? []
            : phRoles
                  .sort(
                      isFeatureEnabled("ff_intntl_eu_editorial_precedence")
                          ? editorialPrecedenceSorter
                          : mrtPrecedenceSorter,
                  )
                  .map((role) => ({
                      title: role.get("title"),

                      // filter an array and join it to display start year,
                      // end year, or both, depending on what we have
                      subtitle: [role.get("start_year"), role.get("end_year") || (role.get("current") && "Current")]
                          .filter((s) => s)
                          .join("-"),

                      displayValue: role.get("org_name"),
                      segueUrl:
                          role.get("org_id") &&
                          `${DjangIO.app.models.QuorumDataType.public_organization.profile}${role.get("org_id")}`,
                      style: DTV_STYLE_CONSTANTS.ELEMENT,
                  }))

        return (
            roleSectionData &&
            roleSectionData.size && {
                title,
                data: roleSectionData,
                id,
                gridWidth,
                sectionComponent,
                tooltipText,
                collapsible: true,
                collapsedGridRows: 2,
            }
        )
    })

export const selectPacOrganizations = ({ sectionComponent, pacOrganizationDataSelector, title, id, gridWidth = 2 }) =>
    createSelector(pacOrganizationDataSelector, (pacOrgs = []) => {
        const sectionData = !pacOrgs
            ? []
            : pacOrgs.map((pacOrg) => ({
                  title: pacOrg.text,

                  segueId: pacOrg.id,
                  segueUrl: `${DjangIO.app.models.QuorumDataType.pacc_pac_organization.profile}${pacOrg.id}`,
                  style: DTV_STYLE_CONSTANTS.RAW_CARD,
              }))
        const formattedTitle = `${title} (${pacOrgs.length})`

        return {
            title: formattedTitle,
            data: sectionData,
            id,
            gridWidth,
            sectionComponent,
            collapsible: true,
            collapsedGridRows: 2,
        }
    })

export const selectScores = ({ sectionComponent }) =>
    createSelector(selectRoleData, selectCookPVI, (roleData, cookPVI) => {
        const scoreData = [
            {
                title: "2020 Partisan Voting Index (PVI)",
                value: cookPVI,
                width: 3,
            },
            {
                title: "Club For Growth",
                value: roleData.cfgscore,
                width: 3,
            },
        ]
        const scoreObj = createSectionObject({
            title: OFFICIAL_SECTION_TITLES.scores,
            data: createDataSections(scoreData),
            id: "officialScoresSections",
            sectionComponent,
        })
        return scoreObj
    })

export const selectIsDlc = (state, props) => props && !props.isProfile

export const selectSocialMediaData = isFeatureEnabled("ff_profile_social_icons")
    ? ({ sectionComponent }) =>
          createSelector(
              selectSocialUsersFromModel(DjangIO.app.person.models.NewPerson.__name__, selectMemberImmutable),
              selectMemberId,
              isCustomOfficial,
              (socialUsers, profileId, isCustomOfficial) => {
                  if (!socialUsers) {
                      return null
                  }

                  const data = []

                  socialUsers.forEach((socialUser) => {
                      const socialUserType = DjangIO.app.social.models.SocialUserType.by_value(
                          socialUser.get("social_user_type"),
                      )

                      data.push({
                          details: generateSocialUserDetails(socialUser, socialUserType),
                          socialUserType,
                          onClick: () => window.open(generateSocialUserUrl(socialUser, socialUserType)),
                          segueToSearchProps: {
                              quorumDataType: DjangIO.app.models.QuorumDataType.document,
                              initialFilter: {
                                  document_type__in: [socialUserType.document_type],
                                  source_members: [profileId],
                                  campaign_source: true,
                              },
                          },
                      })
                  })
                  return createSectionObject({
                      title: OFFICIAL_SECTION_TITLES.socialMedia,
                      data,
                      id: "officialSocialMediaSection",
                      isCustom: isCustomOfficial,
                      sectionComponent,
                      permissions: [DjangIO.app.models.PermissionType.legislative_profiles],
                  })
              },
          )
    : ({ selectorFunctions, sectionComponent, dataItems }) =>
          createSelector(
              socialUserSelectors.selectSocialUsersFromModel(
                  DjangIO.app.person.models.NewPerson.__name__,
                  selectMemberImmutable,
              ),
              selectIsDlc,
              isCustomOfficial,
              (socialUsers, isDlc, isCustomOfficial) => {
                  const socialMediaData = [
                      ...createSocialUserObjects({
                          socialUsers: socialUsers && socialUsers.toJS ? socialUsers.toJS() : socialUsers,
                          selectorFunctions,
                          dataItems,
                          isDlc,
                          sourceKey: "source_members",
                          style: DTV_STYLE_CONSTANTS.MINIMAL,
                      }),
                  ]
                  return createSectionObject({
                      title: OFFICIAL_SECTION_TITLES.socialMedia,
                      data: createDataSections(socialMediaData),
                      id: "officialSocialMediaSection",
                      isCustom: isCustomOfficial,
                      sectionComponent,
                      permissions: [DjangIO.app.models.PermissionType.legislative_profiles],
                  })
              },
          )

export const selectAllStatsData = createSelector(
    selectAllStatsObj,
    (statsObj) =>
        statsObj &&
        Object.entries(statsObj).map(([statEnum, value]) => {
            const field = getStatEnumObject(statEnum).label
            return {
                field,
                value:
                    field && field.toLowerCase().includes("percent")
                        ? `${(value * 100).toFixed()}%`
                        : `${value.toFixed()}`,
            }
        }),
)

// reduce the array of { statName: statValue } objects into one html string
// that will be displayed on the links in the network graph
export const reduceStatNameToValue = (statNameToValueArray) =>
    statNameToValueArray.reduce(
        (labelString, { name, value }) =>
            (labelString +=
                `<strong>${name}</strong>` +
                `<span style="float: right; margin-left: 25px">${+value.toFixed(2)}</span><br/>`),
        "",
    )

export const selectRelationshipGraphData = createSelector(
    selectRelationshipStatsObj,
    selectPersonCache,
    selectMostRecentParty,
    headerTitle,
    (relationshipStats, personCache, party, name) =>
        relationshipStats && {
            edges: createGraphToolTip({
                edgeObj: relationshipStats.edges,
                frequencyField: "Hoyer Score",
                reduceLabelFunction: reduceStatNameToValue,
            }),
            // get all the target nodes, and append the source node of the official's profile
            // this is needed to pass into the relationship graph
            nodes: relationshipStats.edges.reduce((nodeObject, stat, i) => {
                // get the id from the stat id
                const targetId = stat.target.split("-")[1]
                // create the node
                nodeObject[stat.target] = {
                    node_type: getNodeType(personCache[targetId].most_recent_party),
                    name: personCache[targetId].name,
                }
                if (i === 0) {
                    // append the node for the profile currently visited
                    nodeObject[stat.source] = {
                        node_type: getNodeType(party),
                        name,
                    }
                }
                return nodeObject
            }, {}),
        },
)

export const selectLegSessionFilter = createSelector(selectMostRecentRegion, (region) => ({ region }))

export const selectCommittee = makeSelectMemberAttribute("committee")

export const selectWorksFor = makeSelectMemberAttribute("person")
export const selectWorksForId = makeObjSliceSelector(selectWorksFor)("id")
export const selectCurrentMinorRoleTypes = createSelector(
    selectMember,
    (member) => (member && member._ph_dn_current_minor_role_types) || [],
)

// for staffers, only load districts if they have someone they
// work for ie not a committee staffer
export const shouldLoadDistrictsTab = createSelector(
    selectMostRecentPersonType,
    selectWorksForId,
    selectCurrentMinorRoleTypes,
    (personType, worksForId, minorRoleTypes) =>
        personType === DjangIO.app.person.types.PersonType.member.value ||
        (personType === DjangIO.app.person.types.PersonType.staffer.value && !!worksForId) ||
        minorRoleTypes.includes(DjangIO.app.person.newtypes.MinorRoleType.us_governor.value),
)

export const selectStatsDataMobile = createSelector(
    selectAllStatsData,
    (statsData) =>
        statsData && [
            {
                title: "All Stats",
                data:
                    statsData &&
                    statsData.map((data) => ({
                        statName: data.field,
                        statValue: data.value,
                    })),
            },
        ],
)

export const selectOfficialOrStafferDataType = createSelector(selectMostRecentPersonType, (personType) =>
    personType === DjangIO.app.person.types.PersonType.staffer.value
        ? DjangIO.app.models.QuorumDataType.staffer
        : DjangIO.app.models.QuorumDataType.person,
)

export const selectOfficialOrStafferDataTypeValue = createSelector(
    selectOfficialOrStafferDataType,
    (quorumDataType) => quorumDataType.value,
)

export const selectIsFederalOfficial = createSelector(
    selectMostRecentPersonType,
    selectMostRecentRegion,
    (personType, region) =>
        personType === DjangIO.app.person.types.PersonType.member.value &&
        region === DjangIO.app.models.Region.federal.value,
)

export const selectExtraOrgText = createSelector(
    selectIsFederalOfficial,
    selectMostRecentRoleType,
    (isFederal, roleType) =>
        isFederal ? (roleType === DjangIO.app.person.types.RoleType.senator.value ? " in State" : " in District") : "",
)

export const selectCustomImageSection = ({ sectionComponent }) =>
    createSelector(selectCustomDescriptionCustomImageUrl, (imageUrl) =>
        createSectionObject({
            title: OFFICIAL_SECTION_TITLES.customImage,
            data: imageUrl ? [imageUrl] : [],
            id: "officialCustomImage",
            sectionComponent,
        }),
    )

export const selectHasBeenLegislator = makeSelectMemberAttribute("has_been_legislator")

export const selectImmutableMediaOrganizations = createExtraFieldSelector({
    key: "media_organizations",
    objectSelector: makeSelectMemberImmutableAttribute,
    defaultVal: fromJS([]),
})

export const selectPublicOrganizationsData = ({ sectionComponent }) =>
    createSelector(selectImmutableMediaOrganizations, (organizations) => {
        const title = organizations && organizations.count() > 1 ? "Organizations" : "Organization"
        const data = []

        organizations &&
            organizations.map((org) =>
                data.push({
                    qdt: DjangIO.app.models.QuorumDataType.public_organization,
                    title: org.get("name"),
                    text: org.get("major_organization_type"),
                    segueUrl: `/organization/${org.get("id")}/`,
                    imageUrl: org.get("image_url"),
                }),
            )

        const sectionObject = createSectionObject({
            title,
            data,
            id: "officialPublicOrganizationsSection",
            sectionComponent,
            collapsible: true,
        })
        return sectionObject
    })

export const selectLcPersonId = makeSelectMemberAttribute("_ph_lc_person_id")

export const selectCustomDataInfo = makeProfileSelector("allCustomData")

const selectImmutableRegionData = createExtraFieldSelector({
    key: "region_data",
    objectSelector: makeSelectMemberImmutableAttribute,
})

export const selectRegionData = createSelector(selectImmutableRegionData, (regionData) => regionData?.toJS())
